<template>
  <div class="parking-api-test">
    <!-- (1) PageHeader -->
    <ds-page-header title="停车平台接口调试台" subtitle="调用后端开放的停车平台接口，实时查看响应报文与签名信息">
      <template #extra>
        <el-select v-model="communityId" filterable placeholder="选择小区" style="width: 240px">
          <el-option v-for="item in communityList" :key="item.communityId" :label="item.name"
            :value="item.communityId" />
        </el-select>
        <el-button type="primary" icon="el-icon-refresh" @click="_loadApiList">刷新清单</el-button>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <el-alert type="warning" :closable="false" show-icon
        title="写操作接口会产生真实动作（抬杆、扣款、删除月租、扣减商家余额等），调试前请确认不会影响现场业务。"
        description="接口清单、平台地址、签名、车场ID 均由后端维护，前端只能调用清单内已开放的接口。" />
    </div>

    <el-row :gutter="16" style="margin-top: 16px">
      <el-col :xs="24" :sm="24" :md="7" :lg="6">
        <div class="ds-panel api-list-card">
          <div class="ds-panel__title api-list-title">开放接口（{{ apiCount }}）</div>
          <el-menu :default-active="currentKey" class="api-menu" @select="_selectApi">
            <el-submenu v-for="group in groupList" :key="group.name" :index="group.name">
              <template slot="title">
                <span>{{ group.name }}</span>
              </template>
              <el-menu-item v-for="api in group.apis" :key="api.key" :index="api.key">
                <span class="api-no">{{ api.no }}</span>
                <span class="api-title">{{ api.title }}</span>
                <ds-status-tag size="sm" :type="api.type === 'write' ? 'danger' : 'success'"
                  :label="api.type === 'write' ? '写' : '查'" />
              </el-menu-item>
            </el-submenu>
          </el-menu>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="17" :lg="18">
        <div v-if="currentApi" class="ds-panel ds-panel--padded">
          <div class="api-header">
            <div>
              <span class="api-no">{{ currentApi.no }}</span>
              <span class="api-header__title">{{ currentApi.title }}</span>
              <ds-status-tag size="sm" :type="currentApi.type === 'write' ? 'danger' : 'success'"
                :label="currentApi.type === 'write' ? '写操作' : '查询类'" />
            </div>
            <span class="api-path">POST {{ currentApi.path }}</span>
          </div>
          <p class="api-desc">{{ currentApi.desc }}</p>
          <p v-if="autoFields.length > 0" class="api-desc">
            以下字段由服务端自动注入，前端不显示：
            <span v-for="(field, index) in autoFields" :key="field.k">
              <code>{{ field.k }}</code><span v-if="index < autoFields.length - 1">、</span>
            </span>
          </p>

          <el-form label-width="170px" size="small" class="ds-form">
            <el-form-item v-for="field in bizFields" :key="field.k"
              :label="field.k + (field.required ? ' *' : '')">
              <el-select v-if="field.type === 'select'" v-model="formData[field.k]" placeholder="请选择" clearable
                style="width: 100%">
                <el-option v-for="option in field.options" :key="option" :label="option" :value="option" />
              </el-select>
              <el-input v-else v-model="formData[field.k]" :placeholder="field.ph || field.tip" clearable />
              <div v-if="field.tip" class="api-tip">{{ field.tip }}</div>
            </el-form-item>
            <el-form-item v-if="bizFields.length === 0" label="提示">
              <span class="api-tip">该接口无业务参数，直接发送即可</span>
            </el-form-item>
          </el-form>

          <div class="ds-form-actions">
            <el-button type="primary" :loading="sending" @click="_send">发送请求</el-button>
            <el-button @click="_fillExample">填充示例值</el-button>
            <el-button @click="_clearForm">清空</el-button>
          </div>
        </div>
        <div v-else class="ds-panel ds-panel--padded">
          <div class="api-empty">请从左侧选择一个接口</div>
        </div>

        <div class="ds-panel ds-panel--padded" style="margin-top: 16px">
          <el-tabs v-model="activeTab" type="border-card">
            <el-tab-pane label="响应体" name="resp">
              <pre class="api-pre">{{ respText }}</pre>
            </el-tab-pane>
            <el-tab-pane label="请求报文" name="req">
              <pre class="api-pre">{{ reqText }}</pre>
            </el-tab-pane>
            <el-tab-pane label="签名字符串" name="sign">
              <pre class="api-pre">{{ signText }}</pre>
            </el-tab-pane>
            <el-tab-pane label="调用信息" name="meta">
              <pre class="api-pre">{{ metaText }}</pre>
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getParkingApiList, callParkingApi } from '@/api/parking/parkingApiTestApi'
import { getCommunityId, getMyEnteredCommunitys } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ParkingApiTestList',
  components: {
    DsPageHeader,
    DsStatusTag
  },
  data() {
    return {
      communityId: '',
      communityList: [],
      apiMap: {},
      groupList: [],
      currentKey: '',
      formData: {},
      sending: false,
      activeTab: 'resp',
      lastResult: null
    }
  },
  computed: {
    apiCount() {
      return Object.keys(this.apiMap).length
    },
    currentApi() {
      return this.apiMap[this.currentKey] || null
    },
    bizFields() {
      return this.currentApi ? this.currentApi.fields.filter(field => !field.auto) : []
    },
    autoFields() {
      return this.currentApi ? this.currentApi.fields.filter(field => field.auto) : []
    },
    respText() {
      if (!this.lastResult) {
        return '（尚未发起请求）'
      }
      const result = this.lastResult
      if (result.error && !result.response) {
        return '调用错误：' + result.error
      }
      const resp = result.response
      if (resp && typeof resp === 'object') {
        const head = 'status = ' + resp.status + '    message = ' + (resp.message || '') + '\n\n'
        return head + this._pretty(resp)
      }
      return String(resp)
    },
    reqText() {
      if (!this.lastResult || !this.lastResult.requestBody) {
        return '（尚未发起请求）'
      }
      const result = this.lastResult
      const head = 'URL: ' + result.url + '\nHTTP: POST    Content-Type: application/json;charset=UTF-8\n\n'
      return head + this._pretty(result.requestBody)
    },
    signText() {
      if (!this.lastResult || !this.lastResult.signString) {
        return '（尚未发起请求）'
      }
      const result = this.lastResult
      return '待签名字符串 stringA（参数按 ASCII 升序拼接，空值不参与）：\n\n' + result.signString +
        '\n\n签名 sign = MD5(stringA + appKey的MD5大写) = ' + result.sign +
        '\n\n说明：sign 本身不参与签名；timestamp 为 13 位毫秒，平台侧 2 分钟内有效。'
    },
    metaText() {
      if (!this.lastResult) {
        return '（尚未发起请求）'
      }
      const result = this.lastResult
      let text = this._pretty({
        api: result.api,
        method: result.method,
        url: result.url,
        httpStatus: result.httpStatus,
        costMs: result.costMs,
        error: result.error || null,
        errorType: result.errorType || null
      })
      if (result.errorStack) {
        text = text + '\n\n异常堆栈：\n' + result.errorStack
      }
      return text
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._loadCommunityList()
    this._loadApiList()
  },
  methods: {
    _loadCommunityList() {
      getMyEnteredCommunitys({ page: 1, row: 100 }).then(res => {
        this.communityList = res.communitys || []
      }).catch(() => {
        this.communityList = []
      })
    },
    _loadApiList() {
      getParkingApiList().then(res => {
        const body = res.data || {}
        if (body.code !== 0) {
          this.$message.error(body.msg || '加载接口清单失败')
          return
        }
        const apis = (body.data && body.data.apis) || {}
        this.apiMap = apis
        const groups = []
        Object.keys(apis).forEach(key => {
          const api = apis[key]
          let group = groups.find(item => item.name === api.group)
          if (!group) {
            group = { name: api.group || '其他', apis: [] }
            groups.push(group)
          }
          group.apis.push(api)
        })
        this.groupList = groups
        if (!this.currentKey && groups.length > 0) {
          this._selectApi(groups[0].apis[0].key)
        }
      }).catch(() => {
        this.$message.error('加载接口清单失败')
      })
    },
    _selectApi(key) {
      this.currentKey = key
      this.formData = this._buildFormData(key)
      this.lastResult = null
      this.activeTab = 'resp'
    },
    _fillExample() {
      const formData = this._buildFormData(this.currentKey)
      this.bizFields.forEach(field => {
        if (field.ph !== undefined && field.ph !== null && field.ph !== '') {
          formData[field.k] = field.ph
        }
      })
      this.formData = formData
    },
    _buildFormData(key) {
      const api = this.apiMap[key]
      const formData = {}
      if (api && api.fields) {
        api.fields.forEach(field => {
          formData[field.k] = ''
        })
      }
      return formData
    },
    _clearForm() {
      this.formData = {}
    },
    _send() {
      if (!this.currentKey) {
        this.$message.warning('请先选择接口')
        return
      }
      if (!this.communityId) {
        this.$message.warning('请先选择小区')
        return
      }
      const params = {}
      Object.keys(this.formData).forEach(key => {
        const value = this.formData[key]
        if (value === null || value === undefined) {
          return
        }
        const text = String(value).trim()
        if (text === '') {
          return
        }
        params[key] = text
      })
      this.sending = true
      callParkingApi({
        communityId: this.communityId,
        apiKey: this.currentKey,
        params: params
      }).then(res => {
        this.sending = false
        const body = res.data || {}
        if (body.code !== 0) {
          this.lastResult = { ok: false, error: body.msg || '调用失败' }
          this.activeTab = 'resp'
          this.$message.error(body.msg || '调用失败')
          return
        }
        this.lastResult = body.data || {}
        this.activeTab = 'resp'
        if (!this.lastResult.ok) {
          this.$message.warning(this.lastResult.error || '平台调用失败')
        } else {
          this.$message.success('调用完成，耗时 ' + (this.lastResult.costMs || 0) + 'ms')
        }
      }).catch(() => {
        this.sending = false
        this.lastResult = { ok: false, error: '请求异常' }
        this.activeTab = 'resp'
        this.$message.error('请求异常')
      })
    },
    _pretty(obj) {
      try {
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return String(obj)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.parking-api-test {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.api-list-card {
  max-height: 720px;
  overflow-y: auto;
}

.api-list-title {
  padding: var(--ds-spacing-md) var(--ds-spacing-lg);
  border-bottom: 1px solid var(--ds-gray-200);
}

.api-menu {
  border-right: none;
}

.api-menu .el-menu-item {
  padding-left: 30px !important;
}

.api-no {
  color: var(--ds-gray-500);
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  margin-right: 6px;
}

.api-title {
  margin-right: 6px;
}

.api-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--ds-spacing-sm);
  padding-bottom: var(--ds-spacing-md);
  margin-bottom: var(--ds-spacing-md);
  border-bottom: 1px solid var(--ds-gray-200);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
    margin-right: var(--ds-spacing-sm);
  }
}

.api-path {
  color: var(--ds-gray-500);
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}

.api-desc {
  color: var(--ds-gray-700);
  font-size: 13px;
  line-height: 1.8;
  margin: 0 0 12px;
}

.api-tip {
  color: var(--ds-gray-500);
  font-size: 12px;
  line-height: 1.6;
}

.api-empty {
  color: var(--ds-gray-500);
  text-align: center;
  padding: 30px 0;
}

.api-pre {
  background: #0f172a;
  border-radius: 4px;
  color: #e2e8f0;
  font-family: Consolas, Monaco, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  margin: 0;
  max-height: 520px;
  overflow: auto;
  padding: 14px 16px;
  white-space: pre-wrap;
  word-break: break-all;
}

.ds-form-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-spacing-sm);
  padding-top: var(--ds-spacing-md);
  border-top: 1px solid var(--ds-gray-200);
}
</style>
